import { useState, useEffect } from 'react';
import {
  getLeads, createLead, updateLead, deleteLead,
  getProperties, getUnits,
  setLeadStatuses as apiSetStatuses, setLeadStrength as apiSetStrength,
  moveLeadToQuick, setLeadContactLog as apiSetContactLog,
} from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  UserSearch, Plus, Building2, ChevronDown, ChevronRight, AlertCircle,
  StickyNote, ClipboardList, Globe, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { LeadRow } from '@/components/leads/LeadRow';
import { LeadFormDialog } from '@/components/leads/LeadFormDialog';
import { LeadCreatePanel } from '@/components/leads/LeadCreatePanel';
import { NotificationDialog, UnassignedNoteDialog } from '@/components/leads/NotificationDialogs';
import { QuickLeadsList } from '@/components/leads/QuickLeadsList';
import { WebsiteLeadsWindow } from '@/components/leads/WebsiteLeadsWindow';

const emptyForm = {
  name: '', source: '', phone: '', email: '',
  desired_start_date: '', desired_end_date: '',
  potential_unit_ids: [], pets: '', parking_request: '',
  lead_strength: 1,
  converted_to_tenant: false, tenant_id: '',
  price_offered: '', preferred_contact_method: '', notes: '',
  is_unassigned: false, unassigned_note: '', showings: [], statuses: [],
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [defaultViewMode, setDefaultViewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifDialog, setNotifDialog] = useState(null);
  const [notifDate, setNotifDate] = useState('');
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [expandedProps, setExpandedProps] = useState({});
  const [showWebsiteLeads, setShowWebsiteLeads] = useState(false);
  const adminPass = typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '';

  const fetchData = async () => {
    try {
      const [l, p, u] = await Promise.all([getLeads(), getProperties(), getUnits()]);
      setLeads(l);
      setProperties(p);
      setAllUnits(u);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const propMap = {};
  properties.forEach(p => { propMap[p.id] = p; });
  const unitMap = {};
  allUnits.forEach(u => { unitMap[u.id] = u; });

  const unassignedLeads = leads.filter(l => l.is_unassigned || (l.potential_unit_ids || []).length === 0);
  const leadsByProperty = {};
  leads.forEach(lead => {
    if (lead.is_unassigned) return;
    const unitIds = lead.potential_unit_ids || [];
    if (unitIds.length === 0) return;
    const seenProps = new Set();
    unitIds.forEach(uid => {
      const unit = unitMap[uid];
      if (unit && !seenProps.has(unit.property_id)) {
        seenProps.add(unit.property_id);
        if (!leadsByProperty[unit.property_id]) leadsByProperty[unit.property_id] = [];
        leadsByProperty[unit.property_id].push(lead);
      }
    });
  });

  const openCreate = () => setShowCreatePanel(true);

  const openDetail = (lead, viewMode = true) => {
    setEditing(lead);
    setForm({
      id: lead.id,
      name: lead.name || '', source: lead.source || '',
      phone: lead.phone || '', email: lead.email || '',
      desired_start_date: lead.desired_start_date || '',
      desired_end_date: lead.desired_end_date || '',
      potential_unit_ids: lead.potential_unit_ids || [],
      pets: lead.pets || '', parking_request: lead.parking_request || '',
      lead_strength: lead.lead_strength || 1,
      converted_to_tenant: lead.converted_to_tenant || false,
      tenant_id: lead.tenant_id || '',
      price_offered: lead.price_offered || '',
      preferred_contact_method: lead.preferred_contact_method || '',
      notes: lead.notes || '',
      is_unassigned: lead.is_unassigned || false,
      unassigned_note: lead.unassigned_note || '',
      showings: lead.showings || [],
      statuses: lead.statuses || [],
      emailed_dates: lead.emailed_dates || [],
      called_dates: lead.called_dates || [],
      texted_dates: lead.texted_dates || [],
      last_contact_date: lead.last_contact_date || '',
    });
    setDefaultViewMode(viewMode);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = { ...form, price_offered: form.price_offered ? parseFloat(form.price_offered) : null };
    try {
      if (editing) { await updateLead(editing.id, payload); toast.success('Lead updated'); }
      else { await createLead(payload); toast.success('Lead created'); }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save lead');
    } finally { setSaving(false); }
  };

  const handleSaveAsUnassigned = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = { ...form, price_offered: form.price_offered ? parseFloat(form.price_offered) : null, is_unassigned: true, potential_unit_ids: [] };
    try {
      if (editing) { await updateLead(editing.id, payload); toast.success('Lead saved as unassigned'); }
      else { await createLead(payload); toast.success('Lead saved as unassigned'); }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save lead');
    } finally { setSaving(false); }
  };

  const handleContactLogAutosave = async (fields) => {
    if (!editing?.id) return;
    try { await apiSetContactLog(editing.id, fields); fetchData(); }
    catch { toast.error('Failed to save contact log'); }
  };

  const handleDelete = async (id) => {
    try { await deleteLead(id); toast.success('Lead deleted'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete lead'); }
  };

  // Optimistic strength updates so the up/down arrows feel instant.
  const handleSetStrength = async (id, value) => {
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, lead_strength: value } : l));
    try { await apiSetStrength(id, value); }
    catch { toast.error('Failed to update strength'); fetchData(); }
  };

  const handleUpdateStatuses = async (id, nextStatuses) => {
    // assign ids client-side for optimistic render
    const stamped = nextStatuses.map((s) => ({ ...s, id: s.id || `tmp_${Math.random().toString(36).slice(2)}` }));
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, statuses: stamped } : l));
    try {
      const updated = await apiSetStatuses(id, stamped.map(({ id: _id, ...rest }) => rest));
      setLeads((prev) => prev.map((l) => l.id === id ? updated : l));
    } catch { toast.error('Failed to update statuses'); fetchData(); }
  };

  const handleMoveToQuick = async (id) => {
    const lead = leads.find((l) => l.id === id);
    toast(`Move "${lead?.name || 'this lead'}" to Quick Leads?`, {
      description: 'Status tags + showings will be lost.',
      action: {
        label: 'Move',
        onClick: async () => {
          try { await moveLeadToQuick(id); toast.success('Moved to Quick Leads'); fetchData(); }
          catch (e) { toast.error(e.response?.data?.detail || 'Move failed'); }
        },
      },
      cancel: { label: 'Cancel', onClick: () => {} },
      duration: 8000,
    });
  };

  const handleCreateNotification = async () => {
    if (!notifDate) { toast.error('Please enter a notification date'); return; }
    // Task creation temporarily disabled while Tasks module is removed.
    toast.info('Follow-up notifications are temporarily unavailable.');
    setNotifDialog(null);
    setNotifDate('');
  };

  const handleConvertToTenant = (lead) => {
    const convertData = {
      name: lead.name, phone: lead.phone, email: lead.email,
      move_in_date: lead.desired_start_date || '',
      move_out_date: lead.desired_end_date || '',
      potential_unit_ids: lead.potential_unit_ids,
    };
    window.localStorage.setItem('convert_lead', JSON.stringify({ ...convertData, lead_id: lead.id }));
    window.location.href = '/tenants';
  };

  const openNoteDialog = (lead) => { setNoteDialog(lead); setNoteText(lead.unassigned_note || ''); };
  const saveUnassignedNote = async () => {
    if (!noteDialog) return;
    try {
      await updateLead(noteDialog.id, { ...noteDialog, unassigned_note: noteText });
      toast.success('Note saved');
      setNoteDialog(null);
      fetchData();
    } catch { toast.error('Failed to save note'); }
  };

  const rowHandlers = {
    onOpenDetail: (l) => openDetail(l, false),
    onSetStrength: handleSetStrength,
    onUpdateStatuses: handleUpdateStatuses,
    onMoveToQuick: handleMoveToQuick,
    onDelete: handleDelete,
    onConvert: handleConvertToTenant,
    onUpdated: fetchData,
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">Track and manage prospective tenants</p>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <Button onClick={openCreate} data-testid="leads-create-button">
            <Plus className="h-4 w-4 mr-2" /> Add Lead
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setShowWebsiteLeads(true)} data-testid="website-leads-open-btn">
            <Globe className="h-3.5 w-3.5" /> Website Leads
          </Button>
        </div>
      </div>

      <Tabs defaultValue="current" className="space-y-5">
        <TabsList className="bg-slate-100 border p-1">
          <TabsTrigger value="current" className="text-xs gap-1.5 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="leads-tab-current">
            <Users className="h-3.5 w-3.5" /> Current Leads
            <Badge variant="secondary" className="text-[10px] tabular-nums">{leads.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="quick" className="text-xs gap-1.5 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="leads-tab-quick">
            <ClipboardList className="h-3.5 w-3.5" /> Quick Leads
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="space-y-5">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground">Loading...</div>
          ) : leads.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <UserSearch className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="font-heading text-lg font-semibold">No leads yet</h3>
                <p className="text-sm text-muted-foreground mt-1">Start tracking prospective tenants</p>
                <Button className="mt-4" onClick={openCreate}>Add Lead</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {unassignedLeads.length > 0 && (
                <div className="space-y-3">
                  <h2 className="font-heading text-base font-semibold tracking-tight flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    Unassigned Leads
                    <Badge variant="secondary" className="text-xs tabular-nums">{unassignedLeads.length}</Badge>
                  </h2>
                  <Card className="overflow-hidden border-amber-200/50">
                    <div className="space-y-2 p-4">
                      {unassignedLeads.map(lead => (
                        <div key={lead.id}>
                          <LeadRow lead={lead} unitMap={unitMap} {...rowHandlers} />
                          {lead.is_unassigned && (
                            <div className="ml-4 mt-1 flex items-center gap-2">
                              {lead.unassigned_note ? (
                                <div className="flex items-start gap-2 p-2 rounded bg-muted/40 text-xs flex-1">
                                  <StickyNote className="h-3 w-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-muted-foreground">{lead.unassigned_note}</span>
                                  <Button variant="ghost" size="sm" className="h-5 px-1 text-[10px] ml-auto" onClick={() => openNoteDialog(lead)}>Edit</Button>
                                </div>
                              ) : (
                                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 text-muted-foreground" onClick={() => openNoteDialog(lead)}>
                                  <StickyNote className="h-3 w-3" /> Add Note
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              )}

              {Object.entries(leadsByProperty).map(([propId, propLeads]) => {
                const prop = propMap[propId];
                const isOpen = expandedProps[propId] !== false;
                return (
                  <div key={propId} className="space-y-3">
                    <Collapsible open={isOpen} onOpenChange={() => setExpandedProps(prev => ({ ...prev, [propId]: !isOpen }))}>
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <h2 className="font-heading text-base font-semibold tracking-tight">{prop?.name || 'Unknown Property'}</h2>
                          <Badge variant="secondary" className="text-xs tabular-nums">{propLeads.length}</Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <Card className="mt-2 overflow-hidden">
                          <div className="space-y-2 p-4">
                            {propLeads.map(lead => (
                              <LeadRow key={lead.id} lead={lead} unitMap={unitMap} {...rowHandlers} />
                            ))}
                          </div>
                        </Card>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}

              {Object.keys(leadsByProperty).length === 0 && unassignedLeads.length > 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center text-sm text-muted-foreground">
                    No property-assigned leads yet. Create a lead and assign units to see them grouped by property.
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="quick">
          <QuickLeadsList />
        </TabsContent>
      </Tabs>

      <WebsiteLeadsWindow open={showWebsiteLeads} onClose={() => setShowWebsiteLeads(false)} adminPass={adminPass} />

      <LeadCreatePanel
        open={showCreatePanel}
        onClose={() => setShowCreatePanel(false)}
        onCreated={fetchData}
      />
      <LeadFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        onSaveAsUnassigned={handleSaveAsUnassigned}
        saving={saving}
        onLeadUpdated={fetchData}
        onContactLogAutosave={handleContactLogAutosave}
        defaultViewMode={defaultViewMode}
      />
      <NotificationDialog
        notifDialog={notifDialog}
        notifDate={notifDate}
        setNotifDate={setNotifDate}
        onCreateNotification={handleCreateNotification}
        onClose={() => { setNotifDialog(null); setNotifDate(''); }}
      />
      <UnassignedNoteDialog
        noteDialog={noteDialog}
        noteText={noteText}
        setNoteText={setNoteText}
        onSave={saveUnassignedNote}
        onClose={() => setNoteDialog(null)}
      />
    </div>
  );
}
